import { z } from "zod";

/**
 * Shared DTO cho User Global Search (US-E13.F0-0082-S27 / @UC search).
 *
 * TÁCH BIỆT admin-search (`admin-search.ts`, UNSCOPED admin thấy tất cả). Bản USER
 * này membership-scoped: chỉ transactions/members/funds thuộc group/fund của caller.
 * Reuse UTIL admin-search (classifier/cursor/redact) ở service, KHÔNG chia sẻ schema
 * (shape khác: grouped kind transaction|member|fund cho UI mobile, không audit/user PII đầy).
 *
 * BẢO MẬT (invariants):
 * - IDOR: results CHỈ chứa item trong membership caller — service enforce WHERE IN, KHÔNG tin q.
 * - Money: `amountMinor` STRING minor-unit hoặc `null` khi redacted (Member + fund private) —
 *   contract cho phép null/vắng, backend fail-closed quyết định. KHÔNG float.
 * - Cursor opaque (không nhúng PII) — đối xứng text-cursor.ts admin-search.
 *
 * Envelope: `{ results, pageInfo: { nextCursor } }` (nextCursor null = hết trang).
 */

/** Max page-size cho user search. Default (20) do backend áp khi vắng. */
export const USER_SEARCH_MAX_LIMIT = 50;

/** Độ dài tối đa `q` — chặn input phình to (DoS) tại biên. */
export const USER_SEARCH_MAX_QUERY_LENGTH = 256;

/** Query string: `q` bắt buộc (min 1, max 256), cursor opaque optional, limit ∈ [1,50] optional. */
export const userSearchQuerySchema = z
  .object({
    q: z.string().min(1).max(USER_SEARCH_MAX_QUERY_LENGTH),
    cursor: z.string().optional(),
    limit: z.coerce.number().int().min(1).max(USER_SEARCH_MAX_LIMIT).optional(),
  })
  .strict();

export type UserSearchQuery = z.infer<typeof userSearchQuerySchema>;

/** Loại kết quả tra cứu user-scoped (grouped section trên mobile). */
export const userSearchResultKindSchema = z.enum([
  "transaction",
  "member",
  "fund",
]);

export type UserSearchResultKind = z.infer<typeof userSearchResultKindSchema>;

/**
 * 1 kết quả tìm kiếm user-scoped. `amountMinor` string|null (null khi redacted hoặc
 * loại không có tiền — member); `currency`/`fundId` optional theo kind.
 */
export const userSearchResultItemSchema = z
  .object({
    kind: userSearchResultKindSchema,
    id: z.string(),
    label: z.string(),
    sublabel: z.string().nullable().optional(),
    amountMinor: z.string().nullable().optional(),
    currency: z.string().nullable().optional(),
    groupId: z.string(),
    fundId: z.string().nullable().optional(),
  })
  .strict();

export type UserSearchResultItem = z.infer<typeof userSearchResultItemSchema>;

/** Envelope `{ results, pageInfo }` — cursor opaque; nextCursor null = hết. */
export const userSearchResponseSchema = z
  .object({
    results: z.array(userSearchResultItemSchema),
    pageInfo: z
      .object({
        nextCursor: z.string().nullable(),
      })
      .strict(),
  })
  .strict();

export type UserSearchResponse = z.infer<typeof userSearchResponseSchema>;
